import * as yup from 'yup'
import { ActionData } from './action-data.js'
import { FormFieldJson, FormInputJson, FormulaJson, TableColumnJson } from './formula.js'
import { TableCellData, isTableCellData } from './table-cell.js'

export enum LogicType {
  BOOLEAN = 'boolean',
  NOT = 'not',
  AND = 'and',
  OR = 'or',
  COMPARISON = 'comparison',
  UNARY = 'unary',
}

export enum Condition {
  // scalar input CONDITION scalar value
  EQUAL = '=',
  NOT_EQUAL = '!=',
  GREATER_THAN = '>',
  GREATER_THAN_OR_EQUAL = '>=',
  LESS_THAN = '<',
  LESS_THAN_OR_EQUAL = '<=',
  // array/scalar input CONDITION scalar value
  CONTAINS = 'CONTAINS',
  DOES_NOT_CONTAIN = 'DOES NOT CONTAIN',
  // scalar input CONDITION array/scalar value
  IN = 'IN',
  NOT_IN = 'NOT IN',
  // array input CONDITION array value
  CONTAINS_ANY = 'CONTAINS ANY',
  CONTAINS_ALL = 'CONTAINS ALL',
  DOES_NOT_CONTAIN_ANY = 'DOES NOT CONTAIN ANY',
  // array value
  IS_EMPTY = 'IS EMPTY',
  IS_NOT_EMPTY = 'IS NOT EMPTY',
}

// Specific condition types for unary and comparison expressions
export const UNARY_CONDITIONS = [Condition.IS_EMPTY, Condition.IS_NOT_EMPTY] as const
export type UnaryCondition = (typeof UNARY_CONDITIONS)[number]
export type ComparisonCondition = Exclude<Condition, UnaryCondition>

export function isUnaryCondition(condition: Condition): condition is UnaryCondition {
  return UNARY_CONDITIONS.includes(condition as UnaryCondition)
}

export function isComparisonCondition(condition: Condition): condition is ComparisonCondition {
  return !isUnaryCondition(condition)
}
export const COMPARISON_CONDITIONS: ComparisonCondition[] = Object.values(Condition).filter(
  (condition): condition is ComparisonCondition => isComparisonCondition(condition),
)

export const SCALAR_LEFT_SCALAR_RIGHT_CONDITIONS = [
  Condition.EQUAL,
  Condition.NOT_EQUAL,
  Condition.GREATER_THAN,
  Condition.GREATER_THAN_OR_EQUAL,
  Condition.LESS_THAN,
  Condition.LESS_THAN_OR_EQUAL,
  Condition.CONTAINS,
  Condition.DOES_NOT_CONTAIN,
  Condition.IN,
  Condition.NOT_IN,
] as const
export type ScalarLeftScalarRightCondition = (typeof SCALAR_LEFT_SCALAR_RIGHT_CONDITIONS)[number]

/**
 * Returns whether the condition can be applied to an expression where the RHS and LHS are scalar
 * values
 */
export function isScalarLeftScalarRightCondition(
  condition: Condition,
): condition is ScalarLeftScalarRightCondition {
  return SCALAR_LEFT_SCALAR_RIGHT_CONDITIONS.includes(condition as ScalarLeftScalarRightCondition)
}

export const SCALAR_LEFT_ARRAY_RIGHT_CONDITIONS = [Condition.IN, Condition.NOT_IN] as const
export type ScalarLeftArrayRightCondition = (typeof SCALAR_LEFT_ARRAY_RIGHT_CONDITIONS)[number]

/**
 * Returns whether the condition can be applied to an expression where the LHS is a scalar value and
 * the RHS is an array value
 */
export function isScalarLeftArrayRightCondition(
  condition: Condition,
): condition is ScalarLeftArrayRightCondition {
  return SCALAR_LEFT_ARRAY_RIGHT_CONDITIONS.includes(condition as ScalarLeftArrayRightCondition)
}

export const ARRAY_LEFT_SCALAR_RIGHT_CONDITIONS = [
  Condition.CONTAINS,
  Condition.DOES_NOT_CONTAIN,
] as const
export type ArrayLeftScalarRightCondition = (typeof ARRAY_LEFT_SCALAR_RIGHT_CONDITIONS)[number]

/**
 * Returns whether the condition can be applied to an expression where the LHS is an array input and
 * the RHS is a scalar value
 */
export function isArrayLeftScalarRightCondition(
  condition: Condition,
): condition is ArrayLeftScalarRightCondition {
  return ARRAY_LEFT_SCALAR_RIGHT_CONDITIONS.includes(condition as ArrayLeftScalarRightCondition)
}

export const ARRAY_LEFT_ARRAY_RIGHT_CONDITIONS = [
  Condition.EQUAL,
  Condition.NOT_EQUAL,
  Condition.CONTAINS_ALL,
  Condition.CONTAINS_ANY,
  Condition.DOES_NOT_CONTAIN_ANY,
] as const
export type ArrayLeftArrayRightCondition = (typeof ARRAY_LEFT_ARRAY_RIGHT_CONDITIONS)[number]

/**
 * Returns whether the condition can be applied to an expression where the LHS and RHS are array
 * values
 */
export function isArrayLeftArrayRightCondition(
  condition: Condition,
): condition is ArrayLeftArrayRightCondition {
  return ARRAY_LEFT_ARRAY_RIGHT_CONDITIONS.includes(condition as ArrayLeftArrayRightCondition)
}

/**
 * Returns whether the input is a valid `LogicExpression`.
 *
 * This type guard implementation does not actually check the nested expressions for validity. Once
 * recursive yup schemas are implemented, or we switch to a different type schema library that
 * supports it, this should be updated to validate the entire `LogicExpression` structure.
 */
export function isLogicExpression(input: Record<string, unknown>): input is LogicExpression {
  return (
    input &&
    typeof input === 'object' &&
    'type' in input &&
    typeof input.type === 'string' &&
    Object.values(LogicType).includes(input.type.toLowerCase() as LogicType)
  )
}

export function isLogicExpressionJson(
  input: Record<string, unknown>,
): input is LogicExpressionJson {
  return (
    input &&
    typeof input === 'object' &&
    'type' in input &&
    typeof input.type === 'string' &&
    Object.values(LogicType).includes(input.type.toLowerCase() as LogicType)
  )
}

export const logicExpressionSchema = yup.mixed<LogicExpression>((value): value is LogicExpression =>
  isLogicExpression(value),
)

type BooleanExpression = {
  type: LogicType.BOOLEAN
  value: boolean
}

export type NotExpression<T> = {
  type: LogicType.NOT
  expression: BaseLogicExpression<T>
}

type AndExpression<T> = {
  type: LogicType.AND
  expressions: BaseLogicExpression<T>[]
}

type OrExpression<T> = {
  type: LogicType.OR
  expressions: BaseLogicExpression<T>[]
}

export type ComparisonExpression<T> = {
  type: LogicType.COMPARISON
  left: T
  right: T
  condition: ComparisonCondition
}

export type UnaryExpression<T> = {
  type: LogicType.UNARY
  value: T
  condition: UnaryCondition
}

type BaseLogicExpression<T> =
  | BooleanExpression
  | NotExpression<T>
  | AndExpression<T>
  | OrExpression<T>
  | ComparisonExpression<T>
  | UnaryExpression<T>

export type LogicExpressionJson = BaseLogicExpression<
  TableCellData | TableColumnJson | FormulaJson | FormFieldJson | FormInputJson
>
export type LogicExpression = BaseLogicExpression<ActionData | null>
export type LogicComparisonExpression = ComparisonExpression<ActionData | null>
export type LogicUnaryExpression = UnaryExpression<ActionData | null>

const UserEnteredLogicInputSchema = yup.object().shape({
  data: yup
    .mixed<TableCellData>((value): value is TableCellData => isTableCellData(value))
    .nullable()
    .defined(),
  lookupCellIds: yup.array().of(yup.string().required()).nullable().defined(),
  documentIds: yup.array().of(yup.string().required()).nullable().defined(),
  userIds: yup.array().of(yup.string().required()).nullable().defined(),
})
export type UserEnteredLogicInput = yup.InferType<typeof UserEnteredLogicInputSchema>
export type UserEnteredLogicInputs = { [objectId: string]: UserEnteredLogicInput }

export function isUserEnteredLogicInputs(
  input: Record<string, unknown>,
): input is UserEnteredLogicInputs {
  return (
    input &&
    typeof input === 'object' &&
    Object.values(input).every((value) => UserEnteredLogicInputSchema.isValidSync(value))
  )
}

/**
 * Validates each entry of a user-entered logic input map and returns the first failure with the
 * offending object ID and yup message, or undefined when every entry is valid. Complements
 * {@link isUserEnteredLogicInputs}, whose boolean result cannot say which entry failed.
 */
export function validateUserEnteredLogicInputs(
  input: Record<string, unknown>,
): { objectId: string; message: string } | undefined {
  for (const [objectId, value] of Object.entries(input)) {
    try {
      UserEnteredLogicInputSchema.validateSync(value)
    } catch (error) {
      const message = error instanceof yup.ValidationError ? error.message : String(error)
      return { objectId, message }
    }
  }
  return undefined
}
